<template>
  <div>
    <v-card :disabled="isProcessing" :loading="isProcessing">
      <template v-slot:progress>
        <v-progress-linear
          absolute
          color="success"
          height="4"
          indeterminate
        ></v-progress-linear>
      </template>
      <v-tabs
        v-model="tab"
        class="crm-color"
        left
        dark
        icons-and-text
      >
        <v-tabs-slider></v-tabs-slider>
        <v-tab href="#tab-1">
          <v-toolbar-title>{{ title }}</v-toolbar-title>
        </v-tab>
        <v-spacer></v-spacer>
        <div class="buttons-tab"></div>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item value="tab-1">
          <v-card flat>
            <v-card-text>
              <v-form
                @submit.prevent
                ref="requestData"
                v-model="validRequestData"
              >
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="requestData.name"
                      :label="$t('GENERAL.LABELS.NAME') + '*'"
                      required
                      :rules="[validations.required()]"
                      :error-messages="validationErrors.name || []"
                      @change="setChange"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="requestData.email"
                      :label="$t('GENERAL.LABELS.EMAIL') + '*'"
                      required
                      :rules="[validations.required(), validations.email()]"
                      :error-messages="validationErrors.email || []"
                      @change="setChange"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="requestData.phone"
                      :label="$t('GENERAL.LABELS.PHONE')"
                      :error-messages="validationErrors.phone || []"
                      @change="setChange"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="requestData.company_id"
                      :items="companies"
                      item-text="label"
                      item-value="id"
                      :label="$t('GENERAL.LABELS.COMPANY') + '*'"
                      required
                      :rules="[validations.required()]"
                      :error-messages="validationErrors.company_id || []"
                      @change="setChange"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="6">
                    <FieldTagsInput
                      :value="requestData.roles"
                      field-tag-type-slug="company-contact-roles"
                      :label="$t('COMPANY_CONTACTS.ROLE')"
                      :error-messages="roleErrors()"
                      @input="updateRoles"
                      @tag-added="setChange"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <FieldTagsInput
                      :value="requestData.information"
                      field-tag-type-slug="company-contact-information"
                      :label="$t('COMPANY_CONTACTS.INFORMATION')"
                      :error-messages="informationErrors()"
                      @input="updateInformation"
                      @tag-added="setChange"
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      v-model="requestData.notes"
                      :label="$t('COMPANIES.NOTES')"
                      rows="3"
                      auto-grow
                      :error-messages="validationErrors.notes || []"
                      @change="setChange"
                    ></v-textarea>
                  </v-col>
                  <v-col cols="12">
                    <v-checkbox
                      class="mt-0"
                      v-model="requestData.active"
                      :label="$t('GENERAL.LABELS.ACTIVE')"
                      hide-details
                      color="primary"
                      @change="setChange"
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="warning" @click="back(true)">{{ $t("GENERAL.LABELS.BACK_BUTTON") }}</v-btn>
              <v-btn color="success" @click="store()">{{ actionText }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
    <TPConfirm
      v-model="showConfirmSet"
      content="All current changes will be lost."
      @confirm="back"
    ></TPConfirm>
  </div>
</template>

<script>
import validations from "@/views/partials/form/Validations.vue";
import TPConfirm from "@/views/partials/dialogs/Confirm.vue";
import FieldTagsInput from "@/views/partials/form/FieldTagsInput.vue";
import { SHOW_SNACK } from "@/store/snack.module";
import userStateService from "@/common/userState.service.js";
import { GET_COMPANIES_OPTIONS } from "@/store/companies.module";
import {
  GET_COMPANY_CONTACT,
  CREATE_COMPANY_CONTACT,
  UPDATE_COMPANY_CONTACT,
} from "@/store/companyContact.module";

export default {
  name: "EditCompanyContact",
  components: {
    TPConfirm,
    FieldTagsInput,
  },
  data() {
    return {
      validations,
      tab: "tab-1",
      isProcessing: false,
      validRequestData: false,
      hasChanges: false,
      showConfirmSet: false,
      validationErrors: {},
      title: this.$t("COMPANY_CONTACTS.CREATE"),
      actionText: this.$t("GENERAL.LABELS.CREATE_BUTTON"),
      companies: [],
      requestData: {
        company_id: null,
        name: "",
        email: "",
        phone: "",
        notes: "",
        active: true,
        roles: [],
        information: [],
      },
    };
  },
  mounted() {
    this.loadCompanies().then(() => {
      this.selectCompanyContact();
    });
  },
  methods: {
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];

        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }

      if (error && error.message) {
        return error.message;
      }

      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    setValidationErrors(error) {
      this.validationErrors = (error && error.errors) || {};
    },
    clearValidationError(field) {
      if (!this.validationErrors[field]) {
        return;
      }

      this.$delete(this.validationErrors, field);
    },
    loadCompanies() {
      return this.$store
        .dispatch(GET_COMPANIES_OPTIONS, {})
        .then((response) => {
          this.companies = response.data || [];
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        });
    },
    selectCompanyContact() {
      if (!this.$route.params.id) {
        return;
      }

      this.title = this.$t("COMPANY_CONTACTS.EDIT");
      this.actionText = this.$t("GENERAL.LABELS.EDIT_BUTTON");
      this.isProcessing = true;

      this.$store
        .dispatch(GET_COMPANY_CONTACT, this.$route.params.id)
        .then((response) => {
          this.requestData = {
            company_id: response.company_id || null,
            name: response.name || "",
            email: response.email || "",
            phone: response.phone || "",
            notes: response.notes || "",
            active: !!response.active,
            roles: Array.isArray(response.roles)
              ? response.roles.map((role) => ({
                  id: role.id,
                  value: role.value,
                }))
              : [],
            information: Array.isArray(response.information)
              ? response.information.map((tag) => ({
                  id: tag.id,
                  value: tag.value,
                }))
              : [],
          };
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
    setChange() {
      this.hasChanges = true;
      this.clearValidationError("company_id");
      this.clearValidationError("name");
      this.clearValidationError("email");
      this.clearValidationError("phone");
      this.clearValidationError("notes");
      this.clearRoleValidationErrors();
      this.clearInformationValidationErrors();
    },
    updateRoles(roles) {
      this.requestData.roles = roles;
      this.clearRoleValidationErrors();
      this.setChange();
    },
    updateInformation(information) {
      this.requestData.information = information;
      this.clearInformationValidationErrors();
      this.setChange();
    },
    roleErrors() {
      const messages = Object.keys(this.validationErrors)
        .filter((key) => key.startsWith("roles"))
        .flatMap((key) => {
          const err = this.validationErrors[key];
          return Array.isArray(err) ? err : err ? [err] : [];
        });

      return messages;
    },
    clearRoleValidationErrors() {
      Object.keys(this.validationErrors).forEach((key) => {
        if (key.startsWith("roles")) {
          this.$delete(this.validationErrors, key);
        }
      });
    },
    informationErrors() {
      const messages = Object.keys(this.validationErrors)
        .filter((key) => key.startsWith("information"))
        .flatMap((key) => {
          const err = this.validationErrors[key];
          return Array.isArray(err) ? err : err ? [err] : [];
        });

      return messages;
    },
    clearInformationValidationErrors() {
      Object.keys(this.validationErrors).forEach((key) => {
        if (key.startsWith("information")) {
          this.$delete(this.validationErrors, key);
        }
      });
    },
    back(check) {
      if (check && this.hasChanges) {
        this.showConfirmSet = true;
        return;
      }

      this.hasChanges = false;
      this.$router.push({ name: "company-contacts" });
    },
    store() {
      this.$refs.requestData.validate();
      if (!this.validRequestData) {
        return;
      }
      this.validationErrors = {};

      const roles = (this.requestData.roles || []).map((role) => ({
        id: role.id || null,
        value: role.value,
      }));
      const information = (this.requestData.information || []).map((tag) => ({
        id: tag.id || null,
        value: tag.value,
      }));

      let action = CREATE_COMPANY_CONTACT;
      let payload = {
        ...this.requestData,
        roles,
        information,
      };
      if (this.$route.params.id) {
        action = UPDATE_COMPANY_CONTACT;
        payload = {
          id: this.$route.params.id,
          params: {
            ...this.requestData,
            roles,
            information,
          },
        };
      }

      this.isProcessing = true;
      this.$store
        .dispatch(action, payload)
        .then((response) => {
          const blinkId = response && response.id
            ? response.id
            : this.$route.params.id;

          if (blinkId) {
            userStateService.setState(
              "table-state.company-contacts-table.blinkRowItemKey",
              blinkId
            );
          }

          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          });
          this.hasChanges = false;
          this.$router.push({ name: "company-contacts" });
        })
        .catch((error) => {
          this.setValidationErrors(error);
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
  },
};
</script>
